import { useState, useEffect, createContext, useContext } from "react";

// Timer Context for global timer state
export const TimerContext = createContext(null);

export const useTimer = () => {
  const context = useContext(TimerContext);
  if (!context) {
    throw new Error("useTimer must be used within TimerProvider");
  }
  return context;
};

// Timer constants
const NOTIFICATION_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
const AUTO_PAUSE_THRESHOLD_MS = 60 * 60 * 1000; // 60 minutes
const SOUND_REMINDER_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes for sound reminder
const ORIGINAL_TITLE = "BilloraLaw";

// Timer Provider Component - Works in background and persists across tabs/page reloads
export const TimerProvider = ({ children }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [timerProject, setTimerProject] = useState(null);
  const [timerDescription, setTimerDescription] = useState("");
  const [startTime, setStartTime] = useState(null);
  const [accumulatedSeconds, setAccumulatedSeconds] = useState(0);
  
  // Inactivity tracking
  const [lastActivityTime, setLastActivityTime] = useState(() => Date.now());
  const [autoPaused, setAutoPaused] = useState(false);
  const [showInactivityModal, setShowInactivityModal] = useState(false);
  const [pausedAtSeconds, setPausedAtSeconds] = useState(0);
  
  // Track when an entry was saved (used to trigger refresh in TimeTracking)
  const [lastEntrySavedAt, setLastEntrySavedAt] = useState(null);
  
  // Function to notify that an entry was saved
  const notifyEntrySaved = () => {
    setLastEntrySavedAt(Date.now());
  };
  
  // Sound alert settings
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const saved = localStorage.getItem('timerSoundEnabled');
    return saved === 'true';
  });
  const [lastSoundTime, setLastSoundTime] = useState(() => Date.now());

  // Play alert sound using Audio element
  const playAlertSound = () => {
    try {
      const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2LkZSQh3tvYmFteoiRlZKHfG5hX2p4ho+TkIZ5bWFganiGj5KPhntub2NlcHuFjZCMhnpwbGhse4WMj4yGem9ucHN7ho2NiYR6cHB2foiLi4iBd3N2eoSJioiEfnh4fIKGh4WCf3x7foCCg4KAfn19fX5+fn5+fn5+fn5+fn19fX19fXx9fX19fX19fn5+fn5+fn9/f39/gICAgICAgIGBgYGBgYKCgoKCg4ODg4ODhISEhIWFhYWFhoaGhoaHh4eHiIiIiIiJiYmJiYqKioqLi4uLi4yMjIyNjY2Njo6Ojo+Pj4+PkJCQkJGRkZGSkpKSk5OTk5OUlJSVlZWVlpaWlpeXl5eYmJiYmZmZmZqampqbm5ubm5ycnJ2dnZ2enp6en5+fn6CgoKChoaGhoqKioqOjo6OkpKSkpaWlpaampqanp6enqKioqKmpqamqqqqrrKysrK2tra2urq6ur6+vr7CwsLCxsbGxsrKysrOzs7O0tLS0tbW1tba2tra3t7e3uLi4uLm5ubm6urq6u7u7u7y8vLy9vb29vr6+vr+/v7/AwMDAwcHBwcLCwsLDw8PDxMTExMXFxcXGxsbGx8fHx8jIyMjJycnJysrKysvLy8vMzMzMzc3Nzc7Ozs7Pz8/P0NDQ0NHR0dHS0tLS09PT09TU1NTV1dXV1tbW1tfX19fY2NjY2dnZ2dra2trb29vb3Nzc3N3d3d3e3t7e39/f3+Dg4ODh4eHh4uLi4uPj4+Pk5OTk5eXl5ebm5ubn5+fn6Ojo6Onp6enq6urq6+vr6+zs7Ozt7e3t7u7u7u/v7+/w8PDw8fHx8fLy8vLz8/Pz9PT09PX19fX29vb29/f39/j4+Pj5+fn5+vr6+vv7+/v8/Pz8/f39/f7+/v7///8=');
      audio.volume = 0.5;
      audio.play().catch(() => {});
      
      setTimeout(() => {
        const audio2 = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2LkZSQh3tvYmFteoiRlZKHfG5hX2p4ho+TkIZ5bWFganiGj5KPhntub2NlcHuFjZCMhnpwbGhse4WMj4yGem9ucHN7ho2NiYR6cHB2foiLi4iBd3N2eoSJioiEfnh4fIKGh4WCf3x7foCCg4KAfn19fX5+fn5+fn5+fn5+fn19fX19fXx9fX19fX19fn5+fn5+fn9/f39/gICAgICAgIGBgYGBgYKCgoKCg4ODg4ODhISEhIWFhYWFhoaGhoaHh4eHiIiIiIiJiYmJiYqKioqLi4uLi4yMjIyNjY2Njo6Ojo+Pj4+PkJCQkJGRkZGSkpKSk5OTk5OUlJSVlZWVlpaWlpeXl5eYmJiYmZmZmZqampqbm5ubm5ycnJ2dnZ2enp6en5+fn6CgoKChoaGhoqKioqOjo6OkpKSkpaWlpaampqanp6enqKioqKmpqamqqqqrrKysrK2tra2urq6ur6+vr7CwsLCxsbGxsrKysrOzs7O0tLS0tbW1tba2tra3t7e3uLi4uLm5ubm6urq6u7u7u7y8vLy9vb29vr6+vr+/v7/AwMDAwcHBwcLCwsLDw8PDxMTExMXFxcXGxsbGx8fHx8jIyMjJycnJysrKysvLy8vMzMzMzc3Nzc7Ozs7Pz8/P0NDQ0NHR0dHS0tLS09PT09TU1NTV1dXV1tbW1tfX19fY2NjY2dnZ2dra2trb29vb3Nzc3N3d3d3e3t7e39/f3+Dg4ODh4eHh4uLi4uPj4+Pk5OTk5eXl5ebm5ubn5+fn6Ojo6Onp6enq6urq6+vr6+zs7Ozt7e3t7u7u7u/v7+/w8PDw8fHx8fLy8vLz8/Pz9PT09PX19fX29vb29/f39/j4+Pj5+fn5+vr6+vv7+/v8/Pz8/f39/f7+/v7///8=');
        audio2.volume = 0.5;
        audio2.play().catch(() => {});
      }, 300);
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  // Send notification with sound
  const sendSoundNotification = (title, body) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body: body,
        icon: '/billora-dark.png',
        tag: 'timer-sound-reminder',
        requireInteraction: false,
        silent: false
      });
      
      playAlertSound();
      
      setTimeout(() => notification.close(), 5000);
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    }
  };

  // Toggle sound setting
  const toggleSound = () => {
    const newValue = !soundEnabled;
    setSoundEnabled(newValue);
    localStorage.setItem('timerSoundEnabled', newValue.toString());
    
    if (newValue) {
      if ('Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            sendSoundNotification('🔔 Sonido activado', 'Recibirás alertas cada 15 minutos');
          }
        });
      } else if (Notification.permission === 'granted') {
        sendSoundNotification('🔔 Sonido activado', 'Recibirás alertas cada 15 minutos');
      } else {
        playAlertSound();
      }
    }
  };

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Load timer state from localStorage on mount
  useEffect(() => {
    const savedTimer = localStorage.getItem('timerState');
    if (savedTimer) {
      try {
        const state = JSON.parse(savedTimer);
        setIsRunning(state.isRunning);
        setIsPaused(state.isPaused);
        setTimerProject(state.timerProject);
        setTimerDescription(state.timerDescription || "");
        setStartTime(state.startTime);
        setAccumulatedSeconds(state.accumulatedSeconds || 0);
        setLastActivityTime(state.lastActivityTime || Date.now());
        setAutoPaused(state.autoPaused || false);
        
        if (state.isRunning && !state.isPaused && state.startTime) {
          const elapsed = Math.floor((Date.now() - state.startTime) / 1000);
          setSeconds((state.accumulatedSeconds || 0) + elapsed);
          
          const inactivityTime = Date.now() - (state.lastActivityTime || Date.now());
          if (inactivityTime > AUTO_PAUSE_THRESHOLD_MS && !state.autoPaused) {
            const currentSeconds = (state.accumulatedSeconds || 0) + elapsed;
            setPausedAtSeconds(currentSeconds);
            setAutoPaused(true);
            setShowInactivityModal(true);
          }
        } else {
          setSeconds(state.accumulatedSeconds || 0);
        }
      } catch (e) {
        console.error('Error loading timer state:', e);
      }
    }
  }, []);

  // Save timer state to localStorage whenever it changes
  useEffect(() => {
    if (isRunning || timerProject) {
      const state = {
        isRunning,
        isPaused,
        timerProject,
        timerDescription,
        startTime,
        accumulatedSeconds,
        lastActivityTime,
        autoPaused
      };
      localStorage.setItem('timerState', JSON.stringify(state));
    }
  }, [isRunning, isPaused, timerProject, timerDescription, startTime, accumulatedSeconds, lastActivityTime, autoPaused]);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      if (isRunning && !isPaused && !autoPaused) {
        setLastActivityTime(Date.now());
      }
    };

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [isRunning, isPaused, autoPaused]);

  // Format time for display
  const formatTimeForTitle = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}`;
    }
    return `${minutes}m`;
  };

  // Update document title when timer is running
  useEffect(() => {
    if (isRunning && !isPaused && !autoPaused) {
      document.title = `🔴 ${formatTimeForTitle(seconds)} - ${timerProject?.name || 'Timer'} | BilloraLaw`;
    } else if (isRunning && (isPaused || autoPaused)) {
      document.title = `⏸️ ${formatTimeForTitle(seconds)} (Pausado) | BilloraLaw`;
    } else {
      document.title = ORIGINAL_TITLE;
    }

    return () => {
      document.title = ORIGINAL_TITLE;
    };
  }, [isRunning, isPaused, autoPaused, seconds, timerProject]);

  // Sound reminder effect
  useEffect(() => {
    if (!isRunning || isPaused || autoPaused || !soundEnabled) return;

    const checkSoundReminder = () => {
      const now = Date.now();
      const timeSinceLastSound = now - lastSoundTime;
      
      if (timeSinceLastSound >= SOUND_REMINDER_INTERVAL_MS) {
        const timeStr = formatTimeForTitle(seconds);
        sendSoundNotification(
          `🔴 Timer activo: ${timeStr}`,
          `${timerProject?.name || 'Proyecto'} - ¿Sigues trabajando?`
        );
        setLastSoundTime(now);
      }
    };

    const interval = setInterval(checkSoundReminder, 30000);
    checkSoundReminder();
    
    return () => clearInterval(interval);
  }, [isRunning, isPaused, autoPaused, soundEnabled, lastSoundTime, seconds, timerProject]);

  // Check for inactivity and send notifications
  useEffect(() => {
    if (!isRunning || isPaused || autoPaused) return;

    const checkInactivity = () => {
      const inactivityTime = Date.now() - lastActivityTime;
      
      if (inactivityTime >= AUTO_PAUSE_THRESHOLD_MS) {
        setPausedAtSeconds(seconds);
        setAutoPaused(true);
        setShowInactivityModal(true);
        
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('⏸️ Timer pausado automáticamente', {
            body: `Tu timer en "${timerProject?.name}" fue pausado después de 1 hora de inactividad.`,
            icon: '/billora-dark.png',
            tag: 'timer-auto-pause'
          });
        }
        return;
      }
      
      if (inactivityTime >= NOTIFICATION_INTERVAL_MS && inactivityTime < NOTIFICATION_INTERVAL_MS + 60000) {
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('⏱️ Timer activo', {
            body: `Llevas ${formatTimeForTitle(seconds)} en "${timerProject?.name}". ¿Sigues trabajando?`,
            icon: '/billora-dark.png',
            tag: 'timer-reminder'
          });
        }
      }
    };

    const interval = setInterval(checkInactivity, 30000);
    return () => clearInterval(interval);
  }, [isRunning, isPaused, autoPaused, lastActivityTime, seconds, timerProject]);

  // Update seconds based on startTime
  useEffect(() => {
    let interval = null;
    
    const updateSeconds = () => {
      if (isRunning && !isPaused && !autoPaused && startTime) {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setSeconds(accumulatedSeconds + elapsed);
      }
    };

    if (isRunning && !isPaused && !autoPaused) {
      updateSeconds();
      interval = setInterval(updateSeconds, 1000);
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        updateSeconds();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isRunning, isPaused, autoPaused, startTime, accumulatedSeconds]);

  const startTimer = (project, description = "") => {
    const now = Date.now();
    setTimerProject(project);
    setTimerDescription(description);
    setStartTime(now);
    setAccumulatedSeconds(0);
    setSeconds(0);
    setIsRunning(true);
    setIsPaused(false);
    setAutoPaused(false);
    setLastActivityTime(now);
    setShowInactivityModal(false);
    setLastSoundTime(now);
  };

  const pauseTimer = () => {
    if (isRunning && !isPaused && startTime) {
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      setAccumulatedSeconds(prev => prev + elapsed);
      setStartTime(null);
    }
    setIsPaused(true);
  };

  const resumeTimer = () => {
    setStartTime(Date.now());
    setIsPaused(false);
    setAutoPaused(false);
    setLastActivityTime(Date.now());
    setShowInactivityModal(false);
  };

  const resumeFromAutoPause = () => {
    setAccumulatedSeconds(pausedAtSeconds);
    setSeconds(pausedAtSeconds);
    setStartTime(Date.now());
    setAutoPaused(false);
    setIsPaused(false);
    setLastActivityTime(Date.now());
    setShowInactivityModal(false);
  };

  const resumeWithAdjustedTime = (adjustedSeconds) => {
    setAccumulatedSeconds(adjustedSeconds);
    setSeconds(adjustedSeconds);
    setStartTime(Date.now());
    setAutoPaused(false);
    setIsPaused(false);
    setLastActivityTime(Date.now());
    setShowInactivityModal(false);
  };

  const discardExcessTime = () => {
    const adjustedSeconds = Math.max(0, pausedAtSeconds - 3600);
    setAccumulatedSeconds(adjustedSeconds);
    setSeconds(adjustedSeconds);
    setStartTime(Date.now());
    setAutoPaused(false);
    setIsPaused(false);
    setLastActivityTime(Date.now());
    setShowInactivityModal(false);
  };

  const stopTimer = () => {
    setIsRunning(false);
    setIsPaused(false);
    setAutoPaused(false);
    const totalSeconds = seconds;
    setSeconds(0);
    setStartTime(null);
    setAccumulatedSeconds(0);
    setShowInactivityModal(false);
    localStorage.removeItem('timerState');
    document.title = ORIGINAL_TITLE;
    return totalSeconds;
  };

  const resetTimer = () => {
    setIsRunning(false);
    setIsPaused(false);
    setAutoPaused(false);
    setSeconds(0);
    setStartTime(null);
    setAccumulatedSeconds(0);
    setTimerProject(null);
    setTimerDescription("");
    setShowInactivityModal(false);
    localStorage.removeItem('timerState');
    document.title = ORIGINAL_TITLE;
  };

  return (
    <TimerContext.Provider
      value={{
        isRunning,
        isPaused,
        autoPaused,
        seconds,
        timerProject,
        timerDescription,
        setTimerDescription,
        startTimer,
        pauseTimer,
        resumeTimer,
        stopTimer,
        resetTimer,
        showInactivityModal,
        setShowInactivityModal,
        pausedAtSeconds,
        resumeFromAutoPause,
        resumeWithAdjustedTime,
        discardExcessTime,
        soundEnabled,
        toggleSound,
        // Entry saved notification
        lastEntrySavedAt,
        notifyEntrySaved,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};
